import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { initializeKindeClient } from '@/utils/kindeAuth'; // Import the initialize function
import { createPinia } from 'pinia';
import * as Sentry from "@sentry/vue";
import PrimeVue from 'primevue/config';
import Ripple from 'primevue/ripple';
import StyleClass from 'primevue/styleclass';
import Tooltip from 'primevue/tooltip';
import BadgeDirective from 'primevue/badgedirective';
import { TallyPassThrough, TallyPreset } from '@/theme/tallyPreset';

import featureFlagsPlugin from '@/plugins/featureFlags';
import { installChunkReloadRecovery } from '@/utils/chunkReload';

import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice';
import ToastService from 'primevue/toastservice';

import '@/assets/styles.scss';
import '@/assets/tailwind.css';
import '@/assets/metrics.css';
import '@/assets/tally.css';
import { SelectButton } from 'primevue';

const app = createApp(App);
const pinia = createPinia()

// A tab left open across a deploy still asks for the previous build's chunks; reload it onto the
// current build instead of surfacing the failed import (MOMENTUM-ADMIN-BT / BP / BY).
const chunkReloadRecovery = installChunkReloadRecovery(router);

Sentry.init({
    app,
    dsn: "https://67e4fbc58c4534309fc5fdf7682c39ed@o4507896391991296.ingest.us.sentry.io/4508262999130112",
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // The navigation a stale chunk broke still fails while the page reloads onto the current build;
    // that failure is already being recovered from, so don't report it (MOMENTUM-ADMIN-BY).
    beforeSend: (event, hint) => (chunkReloadRecovery.isRecoveringFrom(hint.originalException) ? null : event),
});


app.use(PrimeVue, {
    ripple: true,
    pt: TallyPassThrough,
    theme: {
        preset: TallyPreset,
        options: {
            prefix: 'p',
            darkModeSelector: false || 'none',
            cssLayer: {
                name: 'primevue',
                order: 'tailwind-base, primevue, tailwind-utilities'
            }
        }
    }
});
app.use(ToastService);
app.use(DialogService);
app.use(ConfirmationService);

app.directive('tooltip', Tooltip);
app.directive('badge', BadgeDirective);
app.directive('ripple', Ripple);
app.directive('styleclass', StyleClass);


initializeKindeClient().then(() => {
    // Kinde client is now initialized, so you can proceed with app setup
    app.use(pinia);
    app.use(router);
    app.use(featureFlagsPlugin);
    app.mount('#app');
});
